import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@vd/ui';
import type { ApiError } from '../lib/apiClient';
import { api } from '../lib/api';

interface ClaimError {
  message: string;
  /** True when the token is still valid and a retry could succeed. */
  retryable: boolean;
}

/**
 * Claims a widget handoff token (TOADO-136) and drops the user into the
 * draft it materializes.
 *
 * RequireAuth wraps this route, so an unauthenticated visitor is bounced to
 * sign-in and returned here afterward — meaning by the time this renders
 * there is a session, and the claim either succeeds (redirect to the draft)
 * or reports, in plain language, why it could not.
 */
export function HandoffPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const [error, setError] = useState<ClaimError | null>(null);
  // Bumped by "Try again" to re-run the claim. Terminal failures (404/410/
  // 402) never offer it; a retryable 5xx does, because the server rolled the
  // claim back and the token is still good.
  const [attempt, setAttempt] = useState(0);
  // The claim is single-use, so it must fire exactly once per attempt even
  // under React StrictMode's double-invoked effects. Set synchronously
  // before the async call so the second invocation is a no-op; reset only by
  // a deliberate retry.
  const claimed = useRef(false);

  useEffect(() => {
    if (!token) {
      setError({ message: 'This report link is missing its token.', retryable: false });
      return;
    }
    if (claimed.current) return;
    claimed.current = true;

    api.claimHandoff(token)
      .then(({ ticketId }) => {
        navigate(`/tickets/${ticketId}`, { replace: true, state: { focusDescription: true } });
      })
      .catch((e: unknown) => {
        const status = (e as ApiError).status;
        // A 401 is handled globally (apiClient emits vd:unauthorized, which
        // sends the user to sign-in and back); it never reaches here.
        if (status === 404) {
          setError({ message: "This report can't be opened. It may have expired, or you may not have access to its project.", retryable: false });
        } else if (status === 403) {
          // requireProjectAccess returns 403 for a viewer-role member or a
          // scope-limited token — a permanent permission issue, not transient.
          setError({ message: "You don't have permission to open reports in this project.", retryable: false });
        } else if (status === 410) {
          setError({ message: 'This report link has expired or was already opened.', retryable: false });
        } else if (status === 402) {
          setError({ message: 'This workspace has reached its monthly ticket limit. Ask an owner to raise it, then reopen the report.', retryable: false });
        } else {
          // 5xx / network: the server rolled the claim back, so the token is
          // still valid and a retry can succeed.
          setError({ message: 'We could not open this report just now.', retryable: true });
        }
      });
  }, [token, navigate, attempt]);

  const retry = (): void => {
    claimed.current = false;
    setError(null);
    setAttempt((a) => a + 1);
  };

  return (
    <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center', textAlign: 'center', padding: 24 }}>
      {error
        ? (
          <div style={{ color: 'var(--text-secondary)', maxWidth: 440, lineHeight: 1.5, display: 'grid', gap: 16, justifyItems: 'center' }}>
            <div>{error.message}</div>
            {error.retryable && <Button onClick={retry}>Try again</Button>}
          </div>
        )
        : <div style={{ color: 'var(--text-tertiary)' }}>Opening the full report…</div>}
    </div>
  );
}
